import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import Alert from '@/components/common/Alert';
import FaIcon from '@/components/common/FaIcon';
import TimeAgo from '@/components/common/TimeAgo';
import { SOS_ALARM_RENDER_LIMIT, type SosAlarm } from './sosAlarmModel';

export interface SosAlarmModalProps {
  alarms: SosAlarm[];
  onAcknowledge: (sessionId: string) => void;
  audioArmed: boolean;
  onEnableAudio: () => void;
  socketConnected: boolean;
  pollingEnabled: boolean;
  pollFailing: boolean;
  /**
   * Show responder coordinates (SOS and last known position). Only for viewers
   * with personnel_tracking:view, the permission the polling endpoint already
   * enforces for the same data.
   */
  canViewLocation: boolean;
}

const LOCATION_RESTRICTED = 'Location available to personnel tracking staff';

function formatCoords(lat: number | null, lng: number | null): string | null {
  if (lat === null || lng === null) return null;
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function formatClock(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? time : `${d.toLocaleDateString()} ${time}`;
}

function linkStatus(socketConnected: boolean, pollingEnabled: boolean, pollFailing: boolean): string {
  const live = socketConnected ? 'Live link connected' : 'Live link down';
  if (!pollingEnabled) return `${live} · server check unavailable for this account`;
  if (pollFailing) return `${live} · server check failing, retrying every 10 s`;
  return `${live} · checking the server every 10 s`;
}

function SosAlarmEntry({
  alarm,
  onAcknowledge,
  canViewLocation,
}: {
  alarm: SosAlarm;
  onAcknowledge: (sessionId: string) => void;
  canViewLocation: boolean;
}) {
  const headingId = `sos-alarm-${alarm.sessionId}`;
  const sosCoords = canViewLocation ? formatCoords(alarm.sosLat, alarm.sosLng) : null;
  const lastCoords = canViewLocation ? formatCoords(alarm.lastLat, alarm.lastLng) : null;
  const subtitle = [alarm.callsign, alarm.role].filter(Boolean).join(' · ');

  return (
    <li
      aria-labelledby={headingId}
      data-testid="sos-alarm-entry"
      className="border-l-4 border-[color:var(--severity-critical-spine)] bg-surface-secondary px-4 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={headingId} className="text-lg font-semibold text-theme-primary">
              {alarm.userName}
            </h3>
            {alarm.isExercise === true && (
              <Badge variant="warning" size="md">
                EXERCISE
              </Badge>
            )}
          </div>
          {subtitle && <p className="text-sm text-theme-secondary">{subtitle}</p>}
        </div>
        <Button
          variant="danger"
          onClick={() => onAcknowledge(alarm.sessionId)}
          icon={<FaIcon icon="circle-check" className="h-4 w-4" />}
          aria-label={`Acknowledge SOS from ${alarm.userName}`}
        >
          Acknowledge
        </Button>
      </div>

      {alarm.isExercise === true ? (
        <p className="mt-2 text-sm font-medium text-[color:var(--severity-warning-text)]">
          EXERCISE — this session is under drill incident {alarm.incidentName ?? '(unnamed)'}.
          Confirm by radio: an emergency during a drill can still be real.
        </p>
      ) : (
        alarm.incidentName && (
          <p className="mt-2 text-sm text-theme-secondary">Incident: {alarm.incidentName}</p>
        )
      )}

      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="flex items-center gap-1.5 text-theme-tertiary">
          <FaIcon icon="clock" className="h-3.5 w-3.5" />
          Time of SOS
        </dt>
        <dd className="text-theme-primary">
          {alarm.sosAt ? (
            <>
              <span className="font-mono tabular-nums">{formatClock(alarm.sosAt)}</span>{' '}
              <span className="text-theme-tertiary">
                (<TimeAgo timestamp={alarm.sosAt} intervalMs={10_000} />)
              </span>
            </>
          ) : (
            <span>Not reported</span>
          )}
        </dd>

        <dt className="flex items-center gap-1.5 text-theme-tertiary">
          <FaIcon icon="location-dot" className="h-3.5 w-3.5" />
          SOS location
        </dt>
        <dd className="font-mono tabular-nums text-theme-primary">
          {sosCoords ?? (
            <span className="font-sans">
              {canViewLocation ? 'No location sent with the SOS' : LOCATION_RESTRICTED}
            </span>
          )}
        </dd>

        {lastCoords && (
          <>
            <dt className="flex items-center gap-1.5 text-theme-tertiary">
              <FaIcon icon="location-crosshairs" className="h-3.5 w-3.5" />
              Last known position
            </dt>
            <dd className="text-theme-primary">
              <span className="font-mono tabular-nums">{lastCoords}</span>
              {alarm.lastPositionAt && (
                <span className="text-theme-tertiary">
                  {' '}
                  · <TimeAgo timestamp={alarm.lastPositionAt} intervalMs={10_000} />
                </span>
              )}
            </dd>
          </>
        )}

        {alarm.message && (
          <>
            <dt className="text-theme-tertiary">Message</dt>
            <dd className="text-theme-primary">{alarm.message}</dd>
          </>
        )}
      </dl>

      {alarm.closedOnServer && (
        <p className="mt-2 text-sm text-theme-secondary">
          The server no longer lists this session as in emergency (it may have ended). Acknowledge
          once you have confirmed the responder is safe.
        </p>
      )}
    </li>
  );
}

/**
 * Persistent console SOS alarm. Deliberately undismissable except through an
 * explicit Acknowledge per responder: no close button, no Escape, no backdrop
 * click. The flashing bar is the visual alarm (independent of whether the
 * browser has allowed sound yet); the Modal primitive supplies the focus trap
 * and makes the rest of the console inert while an SOS is unacknowledged.
 */
export default function SosAlarmModal({
  alarms,
  onAcknowledge,
  audioArmed,
  onEnableAudio,
  socketConnected,
  pollingEnabled,
  pollFailing,
  canViewLocation,
}: SosAlarmModalProps) {
  const count = alarms.length;
  const anyExercise = alarms.some((a) => a.isExercise === true);
  const allExercise = count > 0 && alarms.every((a) => a.isExercise === true);
  const shown = count > SOS_ALARM_RENDER_LIMIT ? alarms.slice(0, SOS_ALARM_RENDER_LIMIT) : alarms;
  const notShown = count - shown.length;

  return (
    <Modal
      isOpen={count > 0}
      onClose={() => {
        /* Only Acknowledge clears an SOS. */
      }}
      hideHeader
      closeOnBackdrop={false}
      closeOnEsc={false}
      size="lg"
      ariaLabel={allExercise ? 'EXERCISE SOS alarm' : 'SOS alarm'}
      panelClassName="rounded-lg bg-surface-elevated border-4 border-[color:var(--severity-critical-spine)] shadow-xl"
      footer={
        <div className="space-y-1 text-xs text-theme-tertiary">
          <p>
            Acknowledging silences the alarm on this console only. It does not notify the responder
            or other consoles.
          </p>
          <p className="flex items-center gap-1.5" data-testid="sos-alarm-link-status">
            <FaIcon icon="tower-broadcast" className="h-3.5 w-3.5" />
            {linkStatus(socketConnected, pollingEnabled, pollFailing)}
          </p>
        </div>
      }
    >
      <div
        role="alert"
        data-testid="sos-alarm-banner"
        data-alarm-state="flashing"
        className="sos-alarm-flash flex items-center gap-3 rounded-t-[4px] px-5 py-3"
      >
        <FaIcon icon="triangle-exclamation" className="h-6 w-6 shrink-0" />
        <h2 className="text-xl font-bold uppercase tracking-wide">
          {allExercise ? 'EXERCISE — ' : ''}SOS — responder emergency
        </h2>
        <span className="ml-auto text-sm font-semibold">
          {count} active
          {anyExercise && !allExercise ? ' (includes EXERCISE)' : ''}
        </span>
      </div>

      <div className="space-y-3 px-5 py-4">
        {!audioArmed && (
          <Alert
            variant="warning"
            compact
            icon={<FaIcon icon="volume-xmark" className="h-4 w-4" />}
            action={{ label: 'Enable alarm sound', onClick: onEnableAudio }}
          >
            The browser is blocking alarm sound until this console is clicked or a key is pressed.
          </Alert>
        )}

        <ul className="space-y-3">
          {shown.map((alarm) => (
            <SosAlarmEntry
              key={alarm.sessionId}
              alarm={alarm}
              onAcknowledge={onAcknowledge}
              canViewLocation={canViewLocation}
            />
          ))}
        </ul>

        {notShown > 0 && (
          <p
            data-testid="sos-alarm-overflow"
            className="text-sm font-semibold text-theme-primary"
          >
            {notShown} more open SOS — acknowledge the ones above to see them.
          </p>
        )}
      </div>
    </Modal>
  );
}
